import { useAuth } from '@clerk/clerk-react';
import { Navigate, useLocation } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Client-side route guard for the Platform-Admin SPA.
 *
 * SECURITY MODEL — this guard is a UX layer only. The real authorization
 * boundary is on the server:
 *   1. Clerk issues session JWTs that include a `role` claim sourced from
 *      `public_metadata.role` (configured in Clerk Dashboard → Sessions →
 *      Customize session token).
 *   2. Every admin API endpoint runs through `GlobalAdminAuthGuard`
 *      (Hypebook-API/src/auth/guards/global-admin-auth.guard.ts), which
 *      verifies the JWT signature against Clerk's JWKS and checks
 *      `UserRole.ADMIN` on the user record.
 *   3. The sign-in page (SignIn.tsx) does an additional client-side role
 *      check after Clerk auth completes and bounces non-admins to the
 *      Student app's portal picker.
 *
 * Bypassing this React guard (e.g., disabling JS) only exposes the empty
 * admin UI shell — every data fetch still requires a valid Clerk JWT with
 * the ADMIN role, which an attacker cannot mint.
 */
export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isSignedIn, isLoaded } = useAuth();
  const location = useLocation();

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-gray-600">Validating session…</p>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
